import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { DialogRef, DialogService } from "@ngneat/dialog";
import { TranslateService } from "@ngx-translate/core";
import { slideDownUp } from "src/app/shared/animations";
import { PicklistType } from "src/app/shared/enums/picklist-types";
import { PopupType } from "src/app/shared/enums/popup-types";
import { UserRoles } from "src/app/shared/enums/user-roles";
import { PicklistService } from "src/app/shared/services/picklist.service";
import { Picklist } from "src/app/shared/types/blog-types";
import { showMessage } from "src/app/shared/utils/toast.popup";

@Component({
  selector: "app-picklist-option",
  templateUrl: "./picklist-option.component.html",
  animations: [slideDownUp],
  standalone: false,
})
export class PicklistOptionComponent implements OnInit {
  options: Picklist[] = [];
  id: number | null = null;
  isOpen: { [key: number]: boolean } = {};

  isLoading = false;
  modalTitle!: string;
  modalButton!: string;
  userRole = UserRoles;
  @ViewChild("nameInput") nameInput!: ElementRef;
  @ViewChild("modalTemplate") modalTemplate!: TemplateRef<unknown>;

  form = this.fb.group({
    title: ["", Validators.required],
    description: ["", Validators.required],
    value: ["", Validators.required],
  });

  modal!: DialogRef;
  private dialog = inject(DialogService);

  @Input() title = "category_picklist";
  @Input() type: PicklistType = PicklistType.Service;
  @Input() accordion!: number | undefined;
  @Input() orderNumber!: number;
  @Output() updateActiveAccordion = new EventEmitter<number | undefined>();

  constructor(
    public translate: TranslateService,
    public picklistService: PicklistService,
    public fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.getPicklistOptions();
  }

  getPicklistOptions() {
    const getList = this.picklistService
      .getPicklistOptions(this.type)
      .subscribe({
        next: (res) => {
          getList.unsubscribe();
          this.options = res;
        },
        error: (error) => {
          getList.unsubscribe();
          // eslint-disable-next-line no-console
          console.log(error);
        },
      });
  }

  toggleAccordion(index: number) {
    this.isOpen[index] = !this.isOpen[index];
  }

  async deletePicklistOption(id: number) {
    const deleteOption = this.picklistService
      .deletePickListOption(id)
      .subscribe({
        next: () => {
          deleteOption.unsubscribe();
          showMessage(PopupType.Success, this.translate.instant("category_deleted_successfully"));
          this.getPicklistOptions();
        },
        error: (error) => {
          deleteOption.unsubscribe();
          showMessage(PopupType.Danger, error.toString());
        },
      });
  }

  resetModal() {
    this.id = null;
    this.form.reset();
  }

  openModal() {
    setTimeout(() => {
      if (this.nameInput) {
        this.nameInput.nativeElement.focus();
      }
    }, 0);
    this.resetModal();
    this.modalTitle = this.translate.instant("settings.add_new_item");
    this.modalButton = this.translate.instant("settings.add");
    this.modal = this.dialog.open(this.modalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  openModalForEditing(id: number) {
    setTimeout(() => {
      if (this.nameInput) {
        this.nameInput.nativeElement.focus();
      }
    }, 0);
    this.id = id;
    const item = this.options.find(option => option.id === id);
    if (item) {
      this.form.patchValue({
        title: item.title,
        description: item.description,
        value: item.value,
      });
    }
    this.modalTitle = this.translate.instant("settings.edit_item");
    this.modalButton = this.translate.instant("settings.update");
    this.modal = this.dialog.open(this.modalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  fetchItem() {
    const { title, description, value } = this.form.value;
    return {
      title,
      description,
      delimeter: this.type,
      value,
    };
  }

  addItem() {
    if (this.form.invalid) {
      showMessage(PopupType.Danger, this.translate.instant("please_fill_required_fields"));
      return;
    }

    if (this.id === null) {
      const createSub = this.picklistService.postPickListOptions(this.fetchItem()).subscribe({
        next: () => {
          createSub.unsubscribe();
          this.getPicklistOptions();
          this.modal.close();
          showMessage(PopupType.Success, this.translate.instant("settings.added_successfully"));
        },
        error: (error) => {
          createSub.unsubscribe();
          showMessage(PopupType.Danger, error.toString(), error);
        },
      });
    }
    else {
      const updateSub = this.picklistService.updatePicklistItem(this.fetchItem(), this.id).subscribe({
        next: () => {
          updateSub.unsubscribe();
          this.getPicklistOptions();
          this.modal.close();
          this.id = null;
          showMessage(PopupType.Success, this.translate.instant("settings.updated_successfully"));
        },
        error: (error) => {
          updateSub.unsubscribe();
          showMessage(PopupType.Danger, error.error, error);
        },
      });
    }
  }

  setVisibleAccordion() {
    this.updateActiveAccordion.emit(this.accordion === this.orderNumber ? undefined : this.orderNumber);
  }
}
